import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { get } from "../api";
import type { PortfolioBrief, RevenueMover } from "../api";
import { money, monthLabel, monthParam } from "./format";

/**
 * The month across every customer in view: who moved most, each way.
 *
 * Assembled from the customers' own briefs, not written by a model of its
 * own -- a summary of summaries would be one model's opinion of another's.
 * Ranked within a currency and never across (invariant 49). Each line goes
 * to that customer's month, where the brief and its numbers are.
 */
export default function MonthBrief({ month, user }: { month: string; user: string | null }) {
  const [data, setData] = useState<PortfolioBrief | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setError(null);
    const q = new URLSearchParams({ m: monthParam(month) });
    if (user) q.set("user", user);
    void get<PortfolioBrief>(`/api/revenue/brief?${q}`)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [month, user]);

  if (error) return <div className="error">{error}</div>;
  if (!data) return null;
  if (data.groups.length === 0 && data.awaiting.length === 0) return null;

  return (
    <section className="rev-brief">
      <div className="rev-brief-head">
        <strong>{monthLabel(data.month)} in brief</strong>
      </div>
      {data.groups.map((g) => (
        <div key={g.currency} className="rev-movers">
          {data.groups.length > 1 && <div className="dim">{g.currency}</div>}
          <div className="rev-movers-cols">
            <MoverList title="Up most" items={g.up} total={g.up_count} currency={g.currency} month={data.month} />
            <MoverList title="Down most" items={g.down} total={g.down_count} currency={g.currency} month={data.month} />
          </div>
          {g.steady > 0 && (
            <div className="dim">
              {g.steady} customer{g.steady === 1 ? "" : "s"} steady or mixed.
            </div>
          )}
        </div>
      ))}
      {data.awaiting.length > 0 && (
        <div className="dim">
          Brief not written yet: {data.awaiting.map((c) => c.name).join(", ")}.
        </div>
      )}
    </section>
  );
}

function MoverList({
  title,
  items,
  total,
  currency,
  month,
}: {
  title: string;
  items: RevenueMover[];
  total: number;
  currency: string;
  month: string;
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <div className="rev-movers-t">
        {title}
        {total > items.length && <span className="dim"> · top {items.length} of {total}</span>}
      </div>
      <ul>
        {items.map((m) => (
          <li key={m.slug}>
            <Link to={`/c/${m.slug}/revenue?m=${monthParam(month)}`}>{m.name}</Link>{" "}
            {m.delta !== null && (
              <span className={Number(m.delta) >= 0 ? "pos" : "neg"}>
                {Number(m.delta) >= 0 ? "+" : "−"}
                {money(Math.abs(Number(m.delta)), currency)}
              </span>
            )}
            {m.total !== null && <span className="dim"> to {money(m.total, currency)}</span>}
            <div className="dim rev-mover-h">{m.headline}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}
